import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react"
import type * as React from "react"

import { TableHead } from "@/components/ui/table"
import type { SortOrder } from "@/lib/api"
import { cn } from "@/lib/utils"

export interface SortState {
  sort: string
  order: SortOrder
}

/**
 * Шапка сортируемой колонки. Сортировка идёт на стороне ручки (белый список
 * `RULE_SORT_COLUMNS`), поэтому имя ключа здесь — ровно то, что понимает API.
 *
 * Колонки, которых нет в белом списке, рисуются обычным `TableHead` без стрелок:
 * стрелка, которая ничего не переставляет, — худший вариант, чем её отсутствие.
 */
export function SortHeader({
  column,
  state,
  onSort,
  align = "left",
  className,
  children,
}: {
  column: string
  state: SortState
  onSort: (next: SortState) => void
  align?: "left" | "right" | "center"
  className?: string
  children: React.ReactNode
}) {
  const active = state.sort === column
  const Icon = !active ? ChevronsUpDown : state.order === "asc" ? ChevronUp : ChevronDown

  return (
    <TableHead align={align} className={cn("p-0", className)}>
      <button
        type="button"
        aria-sort={active ? (state.order === "asc" ? "ascending" : "descending") : undefined}
        onClick={() =>
          onSort({ sort: column, order: active && state.order === "asc" ? "desc" : "asc" })
        }
        // `uppercase` повторён явно: preflight сбрасывает text-transform у
        // <button>, и без него шапка сортируемой колонки теряла капс `stencil-label`.
        className={cn(
          "flex w-full items-center gap-1 px-3 py-2 uppercase transition-colors duration-150 ease-snap hover:text-fg",
          active && "text-fg",
          align === "right" && "justify-end",
          align === "center" && "justify-center",
        )}
      >
        <span>{children}</span>
        <Icon
          className={cn("h-3 w-3 shrink-0", active ? "text-accent" : "text-fg-subtle")}
          strokeWidth={2.5}
        />
      </button>
    </TableHead>
  )
}
